import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleDot, faCirclePause, faCirclePlay, faCircleXmark, faComputerSpeaker, faFileAudio, faMicrophone, faPause, faWandMagic } from '@fortawesome/pro-light-svg-icons';
import { makeStyles } from 'tss-react/mui';
import { Box, CircularProgress } from '@mui/material';
import { TranscriptionStatus } from '../../../../hooks/use-transcription';
import { Accordion, AccordionDetails, AccordionSummary } from '../../../../components/accordion';
import { ColorPalette } from '../../../../components/config';
import { Cell } from '../../../../components/cell';
import { AssessmentDialogSupportControlButton } from '../../authority/assessment/assessment-unitabs/assessment-dialog-support/assessment-dialog-support-control-button';
import { AudioVisualizerIcon } from '../../../../components/audio-visualizer/audio-visualizer-icon';
import { Row } from '../../../../components/row';
import { useDialogSupport } from './dialog-support-context';
import { DialogSupportStartModal } from './dialog-support-start-modal';
import { DialogSupportPartialWarningModal } from './dialog-support-partial-warning-modal';
import { DialogSupportErrorModal } from './dialog-support-error-modal';
import { SummaryGenerationStatus, GenerateSummaryResult, MeetingForm, SummaryStatus } from './dialog-support-interfaces';
import { FormDialog } from '../../../../components/form-dialog';
import { useElapsedTimeDisplay } from '../../../../hooks/use-elapsed-time-display';

const useStyles = makeStyles()(() =>
  ({
    customAccordion: {
      '& .MuiAccordionDetails-root': {
        paddingBottom: '0px ',
        marginBottom: '8px',
      },
      '& .MuiAccordionSummary-root': {
        backgroundColor: ColorPalette.DeepGalaxy,
      },
      '& .MuiGrid-root': {
        marginBottom: '3',
      },
    },
    customCell: {
      '& .MuiGrid-root': {
        align: 'right',
      },
    },
    controlContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
    },
    visualizerContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      paddingTop: '5px',
    },
    statusContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      marginLeft: 1,
    },
    statusText: {
      fontSize: '0.875rem',
      color: '#666',
      fontStyle: 'italic',
    },
    buttonRow: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
    },
    dialogContent: {
      marginTop: 10,
    },
  })
);
interface DialogSupportBarProps {
  generateSummary: (transcriptionSessionId: string) => Promise<GenerateSummaryResult>;
  onSummaryGenerated?: (summary: string) => void;
  onGeneratingChange?: (isGenerating: boolean) => void;
  generateButtonLabel?: string;
}

export function DialogSupportBar({ generateSummary, onSummaryGenerated, onGeneratingChange, generateButtonLabel = 'Dan notat' }: DialogSupportBarProps) {
  const { dialogSupportState, setDialogSupportState, transcriptionState, settings, setSettings } = useDialogSupport();
  const { transcriptionStatus, startRecording, stopRecording, startPollingForTranscription, sessionId, micInputAnalyser, systemAudioAnalyser, abortRecording } = transcriptionState;
  const [showPartialWarningModal, setShowPartialWarningModal] = useState(false);
  const [showNoSummaryErrorModal, setShowNoSummaryErrorModal] = useState(false);
  const [showAbortConfirmModal, setShowAbortConfirmModal] = useState(false);
  const { elapsedSeconds, start: startTimer, stop: stopTimer, reset: resetTimer } = useElapsedTimeDisplay();

  const { classes } = useStyles();

  // Control elapsed time based on transcription status
  useEffect(() => {
    switch (transcriptionStatus) {
      case TranscriptionStatus.Active:
        startTimer();
        break;
      case TranscriptionStatus.Inactive:
        resetTimer();
        break;
      default:
        stopTimer();
    }
  }, [transcriptionStatus, startTimer, stopTimer, resetTimer]);

  useEffect(() => {
    onGeneratingChange?.(
      dialogSupportState.summaryGenerationStatus === SummaryGenerationStatus.WaitingForTranscription ||
      dialogSupportState.summaryGenerationStatus === SummaryGenerationStatus.GeneratingSummary
    );
  }, [dialogSupportState.summaryGenerationStatus]);

  function getToggleButtonLabel(): string {
    if (transcriptionStatus === TranscriptionStatus.Paused) return 'Fortsæt';
    if (transcriptionStatus === TranscriptionStatus.Active) return 'Stop';
    return 'Start';
  }

  function getToggleButtonIcon() {
    if (transcriptionStatus === TranscriptionStatus.Active) return faCirclePause;
    return faCirclePlay;
  }

  function handleToggleClick() {
    if (transcriptionStatus === TranscriptionStatus.Inactive) {
      setDialogSupportState(prev => ({
        ...prev,
        isDialogSupportStartModalOpen: true,
      }));
    } else if (transcriptionStatus === TranscriptionStatus.Active) {
      stopRecording();
    } else {
      startRecording(settings.meetingForm === 'Digital', settings.selectedDeviceId);
    }
  }

  function handleGenerateSummary() {
    if (transcriptionStatus === TranscriptionStatus.Active) {
      stopRecording();
    }

    setDialogSupportState(prev => ({
      ...prev,
      summaryGenerationStatus: SummaryGenerationStatus.WaitingForTranscription,
    }));

    if (
      transcriptionStatus !== TranscriptionStatus.Processing &&
      transcriptionStatus !== TranscriptionStatus.Completed &&
      transcriptionStatus !== TranscriptionStatus.ProcessingTimedOut
    ) {
      startPollingForTranscription?.();
    }
  }

  useEffect(() => {
    if (dialogSupportState.summaryGenerationStatus !== SummaryGenerationStatus.WaitingForTranscription) {
      return;
    }

    const isTranscriptionReady = transcriptionStatus === TranscriptionStatus.Completed || transcriptionStatus === TranscriptionStatus.ProcessingTimedOut;

    if (!isTranscriptionReady) {
      return;
    }

    generateSummaryFromTranscription();
  }, [transcriptionStatus, dialogSupportState.summaryGenerationStatus]);

  async function generateSummaryFromTranscription() {
    setDialogSupportState(prev => ({
      ...prev,
      summaryGenerationStatus: SummaryGenerationStatus.GeneratingSummary,
    }));

    try {
      const result = await generateSummary(sessionId);
      handleSummaryResponse(result);
    } catch (error) {
      console.error('Summary generation failed:', error);
      setDialogSupportState(prev => ({
        ...prev,
        summaryGenerationStatus: SummaryGenerationStatus.Failed,
      }));
    }
  }

  function handleSummaryResponse(result: GenerateSummaryResult) {
    const summaryText = result.summary || '';

    setDialogSupportState(prev => ({
      ...prev,
      summaryResult: {
        summary: summaryText,
        status: result.status,
        partialDetails: result.partialDetails,
      },
      summaryGenerationStatus: SummaryGenerationStatus.Completed,
    }));

    if (result.status === SummaryStatus.PartialSummary) {
      setShowPartialWarningModal(true);
    } else if (result.status === SummaryStatus.NoSummary) {
      setShowNoSummaryErrorModal(true);
    } else {
      onSummaryGenerated?.(summaryText);
    }
  }

  function getStatusIcon() {
    if (transcriptionStatus === TranscriptionStatus.Active) return faCircleDot;
    if (transcriptionStatus === TranscriptionStatus.Processing) return faCircleDot;
    return faPause;
  }

  function getStatusText(): string {
    if (dialogSupportState.summaryGenerationStatus === SummaryGenerationStatus.WaitingForTranscription) {
      return 'Venter på transskribering...';
    }
    if (dialogSupportState.summaryGenerationStatus === SummaryGenerationStatus.GeneratingSummary) {
      return 'Genererer notattekst...';
    }
    return formatElapsedTime(elapsedSeconds);
  }

  function formatElapsedTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  function shouldShowSpinner(): boolean {
    return (
      dialogSupportState.summaryGenerationStatus === SummaryGenerationStatus.WaitingForTranscription ||
      dialogSupportState.summaryGenerationStatus === SummaryGenerationStatus.GeneratingSummary
    );
  }

  function handleStartRecording(selectedMeetingForm: MeetingForm, selectedDeviceId?: string) {
    setSettings({
      ...settings,
      meetingForm: selectedMeetingForm,
      selectedDeviceId,
    });
    const includeSystemAudio = selectedMeetingForm === 'Digital';
    startRecording(includeSystemAudio, selectedDeviceId);
  }

  function handleCloseModal() {
    setDialogSupportState(prev => ({
      ...prev,
      isDialogSupportStartModalOpen: false,
    }));
  }

  function handlePartialWarningProceed() {
    setShowPartialWarningModal(false);
    if (dialogSupportState.summaryResult?.summary) {
      onSummaryGenerated?.(dialogSupportState.summaryResult.summary);
    }
  }

  function handleNoSummaryErrorClose() {
    setShowNoSummaryErrorModal(false);
    setDialogSupportState(prev => ({
      ...prev,
      summaryGenerationStatus: SummaryGenerationStatus.Inactive,
    }));
  }

  function handleAbortClick() {
    setShowAbortConfirmModal(true);
  }

  function handleAbortConfirm() {
    abortRecording();
    setDialogSupportState(prev => ({
      ...prev,
      dialogSupportHasBeenRecording: false,
      summaryGenerationStatus: SummaryGenerationStatus.Inactive,
      summaryResult: null,
    }));
    setShowAbortConfirmModal(false);
  }

  function handleAbortCancel() {
    setShowAbortConfirmModal(false);
  }

  return (
    <>
      <DialogSupportStartModal
        open={dialogSupportState.isDialogSupportStartModalOpen}
        closeDialog={handleCloseModal}
        onStartRecording={handleStartRecording}
      />
      <DialogSupportPartialWarningModal open={showPartialWarningModal} partialDetails={dialogSupportState.summaryResult?.partialDetails} onProceed={handlePartialWarningProceed} />
      <DialogSupportErrorModal open={showNoSummaryErrorModal} onClose={handleNoSummaryErrorClose} />
      <FormDialog
        open={showAbortConfirmModal}
        title="Afbryd Dialogstøtte?"
        confirmButtonText="Afbryd"
        cancelButtonText="Annuller"
        onConfirmClick={handleAbortConfirm}
        onCancelClick={handleAbortCancel}
      >
        <Box>
          <Box>Er du sikker på, du ønsker at afbryde Dialogstøtte?</Box>
          <Box sx={{ marginTop: 2 }}>
            Bemærk, at dette sletter al registreret information vedrørende den aktuelle samtale. Hvis du blot ønsker at pause eller stoppe optagelsen, brug knapperne i stedet.
          </Box>
        </Box>
      </FormDialog>
      <Accordion expanded noCollapse className={classes.customAccordion}>
        <AccordionSummary>
          <Box sx={{ color: ColorPalette.WHITE, display: 'flex', width: '100%' }}>
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <FontAwesomeIcon icon={faFileAudio} style={{ paddingRight: '10px' }} size="xl" />
                Dialogstøtte
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Row sx={{ paddingTop: 2 }}>
            <Cell size="quarters">
              <div className={classes.controlContainer}>
                <AssessmentDialogSupportControlButton
                  disabled={
                    transcriptionStatus === TranscriptionStatus.Processing ||
                    dialogSupportState.summaryGenerationStatus === SummaryGenerationStatus.WaitingForTranscription ||
                    dialogSupportState.summaryGenerationStatus === SummaryGenerationStatus.GeneratingSummary
                  }
                  label={getToggleButtonLabel()}
                  icon={getToggleButtonIcon()}
                  onClick={handleToggleClick}
                />
                <AssessmentDialogSupportControlButton
                  disabled={
                    transcriptionStatus === TranscriptionStatus.Inactive ||
                    transcriptionStatus === TranscriptionStatus.Processing ||
                    dialogSupportState.summaryGenerationStatus === SummaryGenerationStatus.WaitingForTranscription ||
                    dialogSupportState.summaryGenerationStatus === SummaryGenerationStatus.GeneratingSummary
                  }
                  label={generateButtonLabel}
                  icon={faWandMagic}
                  onClick={handleGenerateSummary}
                />
              </div>
            </Cell>
            <Cell size="half">
              <div className={classes.visualizerContainer}>
                <AudioVisualizerIcon
                  icon={faMicrophone}
                  name="Mikrofon"
                  analyserNode={micInputAnalyser ?? null}
                  height={35}
                  width={15}
                  color={transcriptionStatus === TranscriptionStatus.Active ? ColorPalette.PrimaryMain : ColorPalette.PrimaryLight}
                  disabled={transcriptionStatus !== TranscriptionStatus.Active}
                />
                {settings.meetingForm === 'Digital' && (
                  <AudioVisualizerIcon
                    icon={faComputerSpeaker}
                    name="Systemlyd"
                    analyserNode={systemAudioAnalyser ?? null}
                    height={35}
                    width={15}
                    color={transcriptionStatus === TranscriptionStatus.Active ? ColorPalette.PrimaryMain : ColorPalette.PrimaryLight}
                    disabled={transcriptionStatus !== TranscriptionStatus.Active}
                  />
                )}
                {transcriptionStatus !== TranscriptionStatus.Inactive && (
                  <Box className={classes.statusContainer}>
                    {shouldShowSpinner() ? (
                      <CircularProgress size={16} sx={{ color: '#666' }} />
                    ) : (
                      <FontAwesomeIcon
                        icon={getStatusIcon()}
                        size="sm"
                        style={{
                          color: transcriptionStatus === TranscriptionStatus.Active || transcriptionStatus === TranscriptionStatus.Processing ? '#e53935' : '#ff9800',
                        }}
                      />
                    )}
                    <Box className={classes.statusText}>{getStatusText()}</Box>
                  </Box>
                )}
              </div>
            </Cell>
            <Cell size="quarters">
              <div className={classes.buttonRow}>
                <AssessmentDialogSupportControlButton
                  disabled={
                    transcriptionStatus === TranscriptionStatus.Processing ||
                    dialogSupportState.summaryGenerationStatus === SummaryGenerationStatus.WaitingForTranscription ||
                    dialogSupportState.summaryGenerationStatus === SummaryGenerationStatus.GeneratingSummary
                  }
                  label="Afbryd"
                  icon={faCircleXmark}
                  onClick={handleAbortClick}
                  width={110}
                />
              </div>
            </Cell>
          </Row>
        </AccordionDetails>
      </Accordion>
    </>
  );
}
